import React from 'react';
import { ArrowRight, Bot, Factory, Lock, LogOut, ShieldCheck, WalletCards } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle, useAuth } from '@ddc-gds/sax-hub-core';
import {
  HUB_COMING_SOON_MODULES,
  HUB_MODULE_DEFINITIONS,
  type HubModuleDefinition,
  type HubModuleId,
} from '../constants/hubModules';

const moduleIcons: Record<HubModuleId, React.ElementType> = {
  ADMINISTRACION: ShieldCheck,
  PRODUCCION: Factory,
  BUSINESS_AGENTS: Bot,
  FINANZAS: WalletCards,
};

export function HubHomePage() {
  const navigate = useNavigate();
  const { availableModules, modulesLoaded, role, user, signOut } = useAuth();
  const userEmail = user?.email ?? 'Usuario';
  const roleLabel = role === 'admin' ? 'Admin global' : role === 'client' ? 'Cliente' : 'Sin rol';

  const availableDefinitions = availableModules
    .map((moduleId) => HUB_MODULE_DEFINITIONS[moduleId])
    .filter((moduleDefinition): moduleDefinition is HubModuleDefinition & { id: HubModuleId } =>
      Boolean(moduleDefinition) && moduleDefinition.id !== 'CORE'
    );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.10),_transparent_28%),linear-gradient(180deg,var(--bg)_0%,var(--surface2)_100%)]">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--primary)] text-white shadow-[var(--shadow-2)]">
            <span className="text-sm font-bold tracking-[0.18em]">SAX</span>
          </div>
          <div>
            <div className="text-lg font-semibold text-[var(--text-primary)]">SAX Hub</div>
            <div className="text-[13px] text-[var(--muted)]">
              La plataforma que ordena, conecta y potencia el negocio.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <div className="hidden rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-2 text-right sm:block">
            <div className="max-w-[260px] truncate text-[var(--text-sm)] font-medium text-[var(--text-primary)]" title={userEmail}>
              {userEmail}
            </div>
            <div className="text-[var(--text-xs)] text-[var(--muted)]">
              Rol: {roleLabel}
            </div>
          </div>
          <button
            onClick={signOut}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 py-2 text-[var(--text-sm)] font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--surface2)]"
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
            Salir
          </button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-col gap-12 px-6 pb-16 pt-6">
        <section className="max-w-4xl">
          <div className="mb-5 h-px w-16 bg-[var(--border)]" />
          <div className="h-6" />
        </section>

        <section className="space-y-6">
          {!modulesLoaded ? (
            <div className="rounded-[24px] border border-[var(--border)] bg-[var(--surface)] p-8 text-[var(--text-sm)] text-[var(--muted)] shadow-[var(--shadow-1)]">
              Resolviendo módulos disponibles...
            </div>
          ) : availableDefinitions.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-[var(--border)] bg-[var(--surface)] p-8">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--surface2)]">
                <Lock className="h-5 w-5 text-[var(--muted)]" />
              </div>
              <h3 className="text-xl font-semibold text-[var(--text-primary)]">Sin módulos activos</h3>
              <p className="mt-2 max-w-2xl text-[var(--text-sm)] text-[var(--muted)]">
                Esta sesión no tiene módulos disponibles. Revisá habilitaciones comerciales o configuración del cliente.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {availableDefinitions.map((moduleDefinition) => {
                const Icon = moduleIcons[moduleDefinition.id];

                return (
                  <button
                    key={moduleDefinition.id}
                    onClick={() => navigate(moduleDefinition.entryPath)}
                    className="group rounded-[28px] border border-[var(--border)] bg-[var(--surface)] px-8 py-7 text-left shadow-[var(--shadow-2)] transition-all hover:-translate-y-1 hover:border-[var(--primary)] hover:shadow-[var(--shadow-3)]"
                  >
                    <div className="mb-10 flex items-start justify-between gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-[var(--primary-light)] text-[var(--primary)]">
                        <Icon className="h-8 w-8" />
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)]">
                        Disponible
                      </div>
                    </div>

                    <h3 className="text-4xl font-semibold tracking-[-0.04em] text-[var(--text-primary)]">{moduleDefinition.title}</h3>
                    <p className="mt-3 max-w-sm text-[15px] leading-7 text-[var(--muted)]">
                      {moduleDefinition.description}
                    </p>
                    <div className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[var(--primary)]">
                      <span>Abrir módulo</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Próximamente</h2>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {HUB_COMING_SOON_MODULES.map((moduleDefinition) => (
              <div
                key={moduleDefinition.id}
                className="rounded-[18px] border border-dashed border-[var(--border)] bg-[color:color-mix(in_srgb,var(--surface)_92%,transparent)] px-5 py-4"
              >
                <h3 className="text-xl font-semibold text-[var(--text-primary)]">{moduleDefinition.title}</h3>
                <p className="mt-1.5 text-[13px] leading-6 text-[var(--muted)]">{moduleDefinition.description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
